import { FiMapPin, FiCheckCircle, FiClock, FiTruck, FiPackage } from "react-icons/fi";
import { FindTranslatedKey } from "../CommonComponents/CustomHooks";

/* ─── color per status ─────────────────────────────────────── */
const getStatusColor = (statusDetail) => {
    const s = statusDetail?.toLowerCase() ?? '';
    if (s.includes('deliver')) return { bg: '#d1fae5', border: '#10b981', dot: '#10b981', text: '#065f46', badge: 'bg-success' };
    if (s.includes('transit') || s.includes('connected')) return { bg: '#dbeafe', border: '#3b82f6', dot: '#3b82f6', text: '#1e40af', badge: 'bg-primary' };
    if (s.includes('pickup')) return { bg: '#fef3c7', border: '#f59e0b', dot: '#f59e0b', text: '#92400e', badge: 'bg-warning text-dark' };
    if (s.includes('cancel')) return { bg: '#fee2e2', border: '#ef4444', dot: '#ef4444', text: '#991b1b', badge: 'bg-danger' };
    if (s.includes('out for')) return { bg: '#ede9fe', border: '#8b5cf6', dot: '#8b5cf6', text: '#5b21b6', badge: 'bg-purple' };
    return { bg: '#f1f5f9', border: '#94a3b8', dot: '#64748b', text: '#334155', badge: 'bg-secondary' };
};

const getIcon = (statusDetail) => {
    const s = statusDetail?.toLowerCase() ?? '';
    if (s.includes('deliver')) return <FiCheckCircle size={15} />;
    if (s.includes('transit') || s.includes('connected')) return <FiTruck size={15} />;
    if (s.includes('pickup')) return <FiPackage size={15} />;
    return <FiClock size={15} />;
};


/* ══════════════════════════════════════════════════════════════
   VERSION 1 — HORIZONTAL STEPPER
══════════════════════════════════════════════════════════════ */
export const TrackingHistoryStepper = ({ movements = [] }) => {
    if (!movements?.length) return null;

    return (
        <div className="card border mt-3">
            <div className="card-header d-flex align-items-center justify-content-between py-2">
                <h6 className="m-0 d-flex align-items-center gap-2">
                    <FiTruck className="text-primary" />
                    {FindTranslatedKey('Tracking History')}
                </h6>
                <span className="badge bg-info text-dark">
                    Total Records: {movements.length}
                </span>
            </div>

            <div className="card-body" style={{ overflowX: 'auto', padding: '28px 20px' }}>
                <div className="d-flex align-items-flex-start" style={{ minWidth: movements.length * 180 }}>
                    {movements.map((item, i) => {
                        const c = getStatusColor(item.statusDetails);
                        const isLast = i === movements.length - 1;
                        return (
                            <div key={i} className="d-flex align-items-flex-start flex-grow-1" style={{ position: 'relative' }}>

                                {/* step block */}
                                <div className="d-flex flex-column align-items-center" style={{ minWidth: 160, flex: 1 }}>

                                    {/* dot + connector line row */}
                                    <div className="d-flex align-items-center w-100" style={{ position: 'relative' }}>
                                        {/* left line */}
                                        {i > 0 && (
                                            <div style={{
                                                flex: 1, height: 3,
                                                background: `linear-gradient(90deg, ${getStatusColor(movements[i - 1].statusDetails).dot}, ${c.dot})`
                                            }} />
                                        )}

                                        {/* dot */}
                                        <div style={{
                                            width: 40, height: 40, borderRadius: '50%',
                                            background: c.bg, border: `3px solid ${c.border}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: c.dot, flexShrink: 0, zIndex: 2,
                                            boxShadow: `0 0 0 4px ${c.bg}`
                                        }}>
                                            {getIcon(item.statusDetails)}
                                        </div>

                                        {/* right line */}
                                        {!isLast && (
                                            <div style={{ flex: 1, height: 3, background: '#e2e8f0' }} />
                                        )}
                                    </div>

                                    {/* label below dot */}
                                    <div className="text-center mt-2 px-2">
                                        <div style={{
                                            fontSize: 12, fontWeight: 600,
                                            color: c.text, lineHeight: 1.3,
                                            marginBottom: 4
                                        }}>
                                            {item.statusDetails || '—'}
                                        </div>
                                        {item.podLocation && (
                                            <div className="d-flex align-items-center justify-content-center gap-1"
                                                style={{ fontSize: 11, color: '#6c757d' }}>
                                                <FiMapPin size={10} style={{ color: c.dot }} />
                                                {item.podLocation}
                                            </div>
                                        )}
                                        <div style={{
                                            fontSize: 11, color: '#94a3b8', marginTop: 3
                                        }}>
                                            {item.statusDate || ''}
                                        </div>
                                        {item.referenceNo && (
                                            <span className={`badge mt-1 ${c.badge}`} style={{ fontSize: 10 }}>
                                                {item.referenceNo}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};


/* ══════════════════════════════════════════════════════════════
   VERSION 2 — VERTICAL TIMELINE
══════════════════════════════════════════════════════════════ */
export const TrackingHistoryTimeline = ({ movements = [] }) => {
    if (!movements?.length) return null;

    return (
        <div className="card border mt-3">
            <div className="card-header d-flex align-items-center justify-content-between py-2">
                <h6 className="m-0 d-flex align-items-center gap-2">
                    <FiMapPin className="text-danger" />
                    {FindTranslatedKey('Tracking History')}
                </h6>
                <span className="badge bg-info text-dark">
                    Total Records: {movements.length}
                </span>
            </div>

            <div className="card-body" style={{ padding: '20px 24px' }}>
                {movements.map((item, i) => {
                    const c = getStatusColor(item.statusDetails);
                    const isLast = i === movements.length - 1;
                    return (
                        <div key={i} className="d-flex gap-3">

                            {/* ── left: dot + line ── */}
                            <div className="d-flex flex-column align-items-center" style={{ width: 40, flexShrink: 0 }}>
                                <div style={{
                                    width: 38, height: 38, borderRadius: '50%',
                                    background: c.bg, border: `2.5px solid ${c.border}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: c.dot, flexShrink: 0, zIndex: 1,
                                    boxShadow: `0 2px 8px ${c.border}40`
                                }}>
                                    {getIcon(item.statusDetails)}
                                </div>
                                {!isLast && (
                                    <div style={{
                                        width: 2, flexGrow: 1, minHeight: 20,
                                        background: `linear-gradient(180deg, ${c.dot}, ${getStatusColor(movements[i + 1].statusDetails).dot})`,
                                        margin: '4px 0', opacity: 0.4
                                    }} />
                                )}
                            </div>

                            {/* ── right: content ── */}
                            <div className="w-100 mb-3" style={{
                                background: c.bg,
                                border: `1px solid ${c.border}30`,
                                borderRadius: 10,
                                padding: '10px 14px',
                            }}>
                                {/* top row */}
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-1 mb-1">
                                    <span style={{ fontWeight: 700, fontSize: 13, color: c.text }}>
                                        {item.statusDetails || '—'}
                                    </span>
                                    <span className={`badge ${c.badge}`} style={{ fontSize: 11 }}>
                                        {item.statusDate || '—'}
                                    </span>
                                </div>

                                {/* bottom row */}
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    {item.podLocation && (
                                        <span className="d-flex align-items-center gap-1"
                                            style={{ fontSize: 12, color: c.text, opacity: 0.8 }}>
                                            <FiMapPin size={11} style={{ color: c.dot }} />
                                            {item.podLocation}
                                        </span>
                                    )}
                                    {item.referenceNo && (
                                        <span style={{ fontSize: 12, color: c.text, opacity: 0.7 }}>
                                            Ref: <strong>{item.referenceNo}</strong>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};